import { useProperty, useTranslation } from '@eti/providers';
import { css, resetButtonStyle } from '@eti/styles';
import { Card, Heading, Inline, Stack, Text, vars } from '@etg/wings';
import { CaretDown } from '@phosphor-icons/react';
import { useCallback, useState } from 'react';
import { ExpandableContent } from '@eti/components';
import type { RefetchCart, RefetchPaymentQuery } from '../../../models';
import DiscountForm from './DiscountForm';

const caretDownStyles = (isOpen: boolean) => css`
  transform: rotateX(${isOpen ? '180deg' : '0deg'});
  transition: transform 0.2s ease-in-out;
`;

const textStyles = css`
  margin-top: 16px;
`;

const inlineStyles = css`
  ${resetButtonStyle}; /* stylelint-disable-next-line */
  cursor: pointer;
  justify-content: space-between;
  text-align: start;
  width: 100%;

  &:focus-visible {
    outline: 2px solid ${vars.colors.focusRing};
  }
`;

interface DiscountProps {
  discountAmount: number;
  isPriceChangeDeclined: boolean;
  refetchCart: RefetchCart;
  refetchPaymentQuery: RefetchPaymentQuery;
}

const Discount = ({ discountAmount, ...rest }: DiscountProps) => {
  const { t } = useTranslation();
  const { p } = useProperty();
  const shouldShowDiscountExpanded = Boolean(p('Payment.DiscountCode.Expanded'));

  const isInitiallyOpen = () => {
    const storedDiscountCode = sessionStorage.getItem('discountCode');
    return storedDiscountCode ? true : shouldShowDiscountExpanded;
  };

  const [isOpen, setIsOpen] = useState(isInitiallyOpen);

  const handleToggle = useCallback(() => {
    setIsOpen((prevState) => !prevState);
  }, []);

  return (
    <Card>
      <Inline
        aria-expanded={isOpen}
        as="button"
        className={inlineStyles}
        data-testid="discount-form-toggle"
        noWrap
        onClick={handleToggle}
      >
        <Heading level={2}>{t('Payment.Discount.Heading')}</Heading>
        <CaretDown
          aria-hidden
          className={caretDownStyles(isOpen)}
          color="#000"
          size={20}
          weight="regular"
        />
      </Inline>
      <ExpandableContent data-testid="discount-form-container" isExpanded={isOpen}>
        <Stack spacing={16}>
          <Text as="span" className={textStyles}>
            {t('Payment.Discount.Toggle.Label')}
          </Text>
          <DiscountForm discountAmount={discountAmount} {...rest} />
        </Stack>
      </ExpandableContent>
    </Card>
  );
};

export default Discount;
